import { FC, KeyboardEvent } from 'react';

import { css } from '@emotion/react';
import { ToucanComponents, ToucanColors } from '@jointoucan/toucan-design';
import { useTheme, useMediaQuery, Box } from '@mui/material';
import { useForm } from 'react-hook-form';

import { InlineTranslation } from '~/components/shared/InlineTranslation';
import Link from '~/components/shared/Link';
import Config from '~/lib/config';

const { Typography, Button, ToucanIcon, TextField } = ToucanComponents;

interface EnterTextProps {
  onTranslatePhrase: (phrase: string) => void;
}

interface EnterTextInputs {
  phrase: string;
}

export const EnterText: FC<EnterTextProps> = ({ onTranslatePhrase }) => {
  const theme = useTheme();
  const isViewportAboveSm = useMediaQuery(theme.breakpoints.up('sm'));
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EnterTextInputs>();

  const onClickTranslate = (inputs: EnterTextInputs) => {
    onTranslatePhrase(inputs.phrase);
  };

  return (
    <div
      css={css`
        background-color: ${ToucanColors.white};
        padding: ${theme.spacing(2)};
        ${theme.breakpoints.up('sm')} {
          padding: ${theme.spacing(4)};
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
        }
      `}
    >
      <Box display="flex" justifyContent="center" alignItems="center">
        <img src="/images/bark/bark-logo.svg" alt="Bark Logo" width="94" height="32" />
        <ToucanIcon
          icon="plus"
          css={css`
            color: ${ToucanColors.blue[400]};
            margin: 0 ${theme.spacing(4)};
          `}
        />
        <Link
          css={css`
            display: flex;
          `}
          href="/?noDashboardRedirect=true"
        >
          <img src="/images/logo-compact.svg" alt="Toucan Logo" width="120" />
        </Link>
      </Box>

      <Typography
        variant={isViewportAboveSm ? 'md' : 'sm'}
        css={css`
          margin-top: ${theme.spacing(2)};
        `}
      >
        BARK has teamed{' '}
        <InlineTranslation sourcePhrase="up" audioFileUrl={`${Config.SITE}/audio/bark/doggo_woof.mp3`}>
          woof
        </InlineTranslation>{' '}
        with the language learning browser extension, Toucan, to help you learn how to{' '}
        <InlineTranslation sourcePhrase="talk" audioFileUrl={`${Config.SITE}/audio/bark/doggo_ruff.mp3`}>
          ruff
        </InlineTranslation>{' '}
        to your furry friend. Try it out!
      </Typography>

      <Box
        display="flex"
        flexDirection="column"
        mt={2}
        css={css`
          ${theme.breakpoints.up('sm')} {
            flex-direction: row;
            align-items: flex-start;
            margin-top: ${theme.spacing(4)};
          }
        `}
      >
        <TextField
          label="Enter your text"
          placeholder="Do you want a snack?"
          {...register('phrase', {
            required: 'Add a word or phrase here to translate it.',
            maxLength: {
              value: 250,
              message: 'You can only translate phrases up to 250 characters.',
            },
            pattern: {
              value: /[\w]+/,
              message: 'Looks like we can’t translate this. Try something else!',
            },
          })}
          hasError={!!errors.phrase}
          helperText={!!errors.phrase && errors.phrase.message}
          onKeyDown={(event: KeyboardEvent) => {
            if (event.key === 'Enter') {
              handleSubmit(onClickTranslate)();
            }
          }}
        />
        <Button
          type="utility"
          size="large"
          onClick={handleSubmit(onClickTranslate)}
          css={css`
            // Using Bark colors
            background-color: #1a4e8a;
            &:hover {
              background-color: #007cc2;
            }
            &:active {
              background-color: #007cc2;
              box-shadow: inset 0px 0px 0px 4px #1a4e8a;
            }
            margin-top: ${theme.spacing(2)};
            ${theme.breakpoints.up('sm')} {
              margin-top: 0;
              margin-left: ${theme.spacing(2)};
            }
          `}
        >
          Translate
        </Button>
      </Box>
    </div>
  );
};
